import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { authService } from '../services/authService'

interface ProtectedRouteProps {
  children: ReactNode
  requirePermission?: string
  requireRole?: 'admin' | 'doctor' | 'nurse' | 'receptionist'
}

export function ProtectedRoute({ children, requirePermission, requireRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasPermission, hasRole } = useAuth()
  const location = useLocation()

  // Check localStorage as fallback if React state hasn't updated yet
  // This fixes race condition where login sets localStorage but React state hasn't updated
  // hasPermission and hasRole already check localStorage internally, so we only need to fix isAuthenticated
  const userFromStorage = authService.getCurrentUser()
  const actuallyAuthenticated = isAuthenticated || !!userFromStorage

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    )
  }

  if (!actuallyAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (requirePermission && !hasPermission(requirePermission)) {
    return (
      <div className="error-container">
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    )
  }

  if (requireRole && !hasRole(requireRole)) {
    return (
      <div className="error-container">
        <h2>Access Denied</h2>
        <p>This page requires {requireRole} role.</p>
      </div>
    )
  }

  return <>{children}</>
}

